import { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { Home, Music, Users, Settings, ListMusic, Mic2, Download, MessageSquare, Image, ChevronLeft, ChevronRight } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { cn } from '@/lib/utils';
import { UserDropdown } from '@/components/ui/UserDropdown';

interface NavItem {
  path: string;
  label: string;
  icon: React.ReactNode;
}

export function Sidebar() {
  const { user } = useAuth();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const isAdmin = user?.role === 'SUPERADMIN' || user?.role === 'MUZIEKCOMMISSIE';
  const hasDirigentAccess = user?.role && ['DIRIGENT', 'MUZIEKCOMMISSIE', 'SUPERADMIN'].includes(user.role);

  const navItems: NavItem[] = [
    { path: '/', label: 'Home', icon: <Home className="w-5 h-5" /> },
    { path: '/repertoire', label: 'Repertoire', icon: <Music className="w-5 h-5" /> },
    { path: '/playlists', label: 'Afspeellijsten', icon: <ListMusic className="w-5 h-5" /> },
    { path: '/downloads', label: 'Downloads', icon: <Download className="w-5 h-5" /> },
    { path: '/berichten', label: 'Berichten', icon: <MessageSquare className="w-5 h-5" /> },
    { path: '/media', label: 'Media', icon: <Image className="w-5 h-5" /> },
    ...(hasDirigentAccess ? [{ path: '/dirigent', label: 'Dirigent', icon: <Mic2 className="w-5 h-5" /> }] : []),
    { path: '/smoelenboek', label: 'Smoelenboek', icon: <Users className="w-5 h-5" /> },
    ...(isAdmin ? [{ path: '/admin', label: 'Admin', icon: <Settings className="w-5 h-5" /> }] : []),
  ];

  return (
    <aside
      className={cn(
        'hidden lg:flex flex-col bg-bg-elevated border-r border-subtle h-screen sticky top-0 transition-all duration-300',
        isCollapsed ? 'w-20' : 'w-64'
      )}
    >
      {/* Logo */}
      <div className={cn(
        'p-6 border-b border-subtle flex items-center',
        isCollapsed ? 'justify-center' : 'gap-3'
      )}>
        <NavLink to="/" className="flex items-center gap-3">
          <img
            src="https://dev.popkoorzzing.nl/wp-content/uploads/2023/06/Logo_ZZing_tr.png"
            alt="Zzing"
            className="h-10"
          />
          {!isCollapsed && (
            <span className="text-xl font-bold text-zzing-purple-500">Leden App</span>
          )}
        </NavLink>
      </div>

      {/* Collapse Toggle */}
      <button
        onClick={() => setIsCollapsed(!isCollapsed)}
        className="absolute -right-3 top-20 w-6 h-6 bg-bg-elevated border border-subtle rounded-full flex items-center justify-center text-text-muted hover:text-text-primary hover:bg-bg-elevated-hover transition-colors z-10"
        title={isCollapsed ? 'Sidebar uitklappen' : 'Sidebar inklappen'}
      >
        {isCollapsed ? (
          <ChevronRight className="w-4 h-4" />
        ) : (
          <ChevronLeft className="w-4 h-4" />
        )}
      </button>

      {/* Navigation */}
      <nav className="flex-1 px-3 py-4 space-y-1">
        {navItems.map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            title={isCollapsed ? item.label : undefined}
            className={({ isActive }) =>
              cn(
                'flex items-center gap-3 px-4 py-3 rounded-lg font-medium transition-all duration-200',
                isActive
                  ? 'bg-zzing-purple-500 text-white'
                  : 'text-text-secondary hover:text-text-primary hover:bg-bg-elevated-hover',
                isCollapsed && 'justify-center px-3'
              )
            }
          >
            {item.icon}
            {!isCollapsed && <span>{item.label}</span>}
          </NavLink>
        ))}
      </nav>

      {/* User section with dropdown */}
      {user && (
        <div className={cn(
          'p-4 border-t border-subtle',
          isCollapsed && 'px-2'
        )}>
          {isCollapsed ? (
            // Collapsed: just show avatar
            <NavLink
              to="/instellingen"
              className="flex items-center justify-center"
              title={user.name}
            >
              <div className="w-10 h-10 rounded-full overflow-hidden bg-zzing-purple-500/20 flex items-center justify-center">
                {user.photo ? (
                  <img src={user.photo} alt={user.name} className="w-full h-full object-cover" />
                ) : (
                  <span className="text-sm text-zzing-purple-500 font-semibold">
                    {user.name.charAt(0).toUpperCase()}
                  </span>
                )}
              </div>
            </NavLink>
          ) : (
            // Expanded: show dropdown
            <UserDropdown />
          )}
        </div>
      )}
    </aside>
  );
}
